// src/belediye/components/BelediyeSidebar.js

import React, { useEffect, useRef, useMemo } from "react";
import { NavLink } from "react-router-dom";
import {
  Bars3Icon,
  XMarkIcon, // FAZ-92: telefonda çekmecenin kapat düğmesi
  BellIcon,
  HomeIcon,
  InboxArrowDownIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathRoundedSquareIcon,
  PresentationChartBarIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  UsersIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon,

  //   FAZ-24 yönetim ekranları
  MapPinIcon,
  MapIcon,
  IdentificationIcon,

  //   Halk Masası ikonları

  MagnifyingGlassIcon,
  PlusCircleIcon,

  //   FAZ-56: "Görev Ata" maddesi — anketlerle aynı ikonu paylaşmasın diye ayrı
  PaperAirplaneIcon,

  //   FAZ-28 Kurum İçi Görevler

  //   Kurum İçi Mesajlaşma

} from "@heroicons/react/24/outline";

import "./Sidebar.css";
// Dogansoft — firma logosu (sidebar alt bilgi)
import logo from "../assets/images/dogansoft-logo.jpeg";
// FAZ-92: çekmecenin tepesinde kurum logosu (kullanıcı: "sidebar açılınca
// soldan, yani burada logo kısmı olsun — logo açılan pencereden gelsin")
import kimLogo from "../assets/images/kim_logo.png";

//   FAZ-12: izinli sidebar (permission_ui şalteri) — NAV tek kaynak
import usePermissions from "../hooks/usePermissions";
// FAZ-87: ekran katmanı tek kaynaktan (bkz. belediye/olcu.js)
import useKatman from "../hooks/useKatman";
import { visibleNav } from "../nav";
// FAZ-77: GorevNavBadge içe aktarımı kaldırıldı (menü rozetleri söküldü).
// FAZ-51: kavram ikonları TEK sözlükten
import { TaskIcon, ChatModuleIcon } from "./icons/appIcons";
import { useSelector } from "react-redux";
import { selectEnabledModules } from "../redux/slices/authzSlice";
// FAZ-85: menüdeki bildirim ikonu okunmamış varken nokta taşır. Ek istek YOK —
// zil zaten aynı store'u dolduruyor, buradan yalnız okunuyor.
import { selectHasUnread } from "../redux/slices/belediyeNotificationsSlice";

const HM_LS = {
  lastCitizenId: "hm_lastCitizenId",
};

//   FAZ-12: NAV sayfa anahtarı → sunum (etiket + ikon). NAV saf veri kalır;
//   sunum sidebar'ın işi. Etiketler bugünkü menüyle birebir.
const NAV_PRESENTATION = {
  // Tek sekmeli "Başvurular" ekranı (4 statü sekme olarak içeride)
  basvurular: { label: "Başvurular", icon: InboxArrowDownIcon },
  // Eski statü anahtarları (nav.js artık üretmiyor; geriye-uyum için duruyor)
  yeni: { label: "Yeni", icon: InboxArrowDownIcon },
  incelemede: { label: "İncelemedekiler", icon: WrenchScrewdriverIcon },
  cozulen: { label: "Çözülenler", icon: CheckCircleIcon },
  cozulemeyen: { label: "Çözülemeyenler", icon: XCircleIcon },
  yonlendirilen: { label: "Yönlendirilenler", icon: ArrowPathRoundedSquareIcon },
  raporlar: { label: "Raporlar", icon: PresentationChartBarIcon },
  anketler: { label: "Anketler", icon: ClipboardDocumentCheckIcon },
  "hm-yeni": { label: "Başvuru Oluştur", icon: PlusCircleIcon },
  "hm-sorgu": { label: "Sorgu", icon: MagnifyingGlassIcon },
  // FAZ-51: görev kavramı artık TaskIcon (eskiden UserGroupIcon idi ve
  // "Personel" maddesiyle AYNI ikonu paylaşıyordu).
  gorevlerim: { label: "Gelen Talepler", icon: TaskIcon },
  personel: { label: "Personel", icon: UserGroupIcon },
  // FAZ-56 — Kurum İçi Görevler İKİYE AYRILDI. "gorevlerim" anahtarı SAHA
  // akışınındır (yukarıda) — kurum içi "bana atananlar" sayfası "gorev-bana".
  // ("gorev-rapor" üçüncü madde olarak vardı, 2026-07-28'de kaldırıldı.)
  "gorev-ata": { label: "Talep Gönder", icon: PaperAirplaneIcon },
  "gorev-bana": { label: "Gelen Talepler", icon: TaskIcon },
  mesajlar: { label: "Mesajlar", icon: ChatModuleIcon },
  "sy-kullanicilar": { label: "Kullanıcılar", icon: UsersIcon },
  "sy-roller": { label: "Roller", icon: ShieldCheckIcon },
  "sy-birim-agaci": { label: "Birim Ağacı", icon: BuildingOffice2Icon },
  "sy-mahalleler": { label: "Mahalleler", icon: MapPinIcon },
  "sy-idari-sinirlar": { label: "İdari Sınırlar", icon: MapIcon },
  "sy-vatandaslar": { label: "Vatandaşlar", icon: IdentificationIcon },
};

//   FAZ-12: visibleNav çıktısını bugünkü sections yapısına çevirir (aynı
//   görsel bileşenler/CSS — yalnız içerik kaynağı değişir). Saf fonksiyon.
export function buildSectionsFromNav(navItems, basePath) {
  const sections = [];
  const byLabel = new Map();

  navItems.forEach((item) => {
    const pres = NAV_PRESENTATION[item.page];
    if (!pres) return; // sunumu tanımsız sayfa menüye giremez

    let section = byLabel.get(item.group);
    if (!section) {
      section = { key: item.group, label: item.group, items: [] };
      byLabel.set(item.group, section);
      sections.push(section);
    }

    const to = item.href.startsWith("/")
      ? item.href
      : `${basePath}/${item.href}`.replace(/\/{2,}/g, "/");

    section.items.push({ key: item.page, label: pres.label, to, icon: pres.icon });
  });

  return sections;
}

const BelediyeSidebar = ({ user, unitSlug, isExpanded, onToggle }) => {
  const isBaşkan = user?.role === "başkan";
  const isHalkMasasi = user?.role === "personel_2";

  //   FAZ-12: izinli yol şalteri (uiOn=false → aşağıdaki MEVCUT yol aynen)
  const { uiOn, permissions, scope, uiProfile } = usePermissions();
  // FAZ-28+ modül kapısı: /me.enabled_modules (görev maddelerinin gizlenmesi için)
  const enabledModules = useSelector(selectEnabledModules);
  const hasUnread = useSelector(selectHasUnread);

  const sidebarRef = useRef(null);

  /* -------------------------------------------------------------
     EKRAN KATMANI (FAZ-87)
     Eskiden burada kendi `resize` dinleyicisi vardı: her piksel için
     setState çağırıyordu ve 768px eşiği sistemin geri kalanından
     sapıyordu. Artık tek kaynak olcu.js.
  ------------------------------------------------------------- */
  const katmanAdi = useKatman();
  const telefon = katmanAdi === "telefon";
  const tablet = katmanAdi === "tablet";

  /* -------------------------------------------------------------
     DIŞARI TIKLANINCA SIDEBAR KAPANSIN (SADECE AÇIKKEN)
  ------------------------------------------------------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!isExpanded) return;
      // FAZ-87: perde AYRI bir kapanma yolu. Burada da sayılırsa tek
      // tıklamada iki kez toggle olur (kapanır ve hemen geri açılır).
      if (e.target.closest && e.target.closest(".sidebar-perde")) return;
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        onToggle();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isExpanded, onToggle]);

  /* -------------------------------------------------------------
     FAZ-87 — ÇEKMECE AÇIKKEN ARKA SAYFA KAYMASIN
     Desen InfoPopover.js:82-89'dan alındı. YALNIZ TELEFON: tablette ray
     itmeli çalışır, arka sayfa okunmaya devam etmeli.
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!telefon || !isExpanded) return undefined;
    const eski = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = eski;
    };
  }, [telefon, isExpanded]);

  /* -------------------------------------------------------------
     FAZ-87 — ESC KAPATIR
     Çekmece bir diyalog gibi davrandığına göre klavyeyle de kapanmalı.
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!isExpanded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onToggle();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isExpanded, onToggle]);

  /* -------------------------------------------------------------
     ROUTE YARDIMCILARI
  ------------------------------------------------------------- */

  //   Halk Masası: unitSlug’a bağlı değil
  const basePath = isHalkMasasi
    ? "/yonetim/halk-masasi"
    : isBaşkan
    ? "/yonetim/baskanlik"
    : `/yonetim/${unitSlug || ""}`.replace(/\/+$/, "");

  // FAZ-22 4b: legacy (uiOn=false) sidebar de canonical (birimSİZ) üretir; tüm
  // canonical route'lar mevcut, eski :unitSlug alias'ları korunur.
  const applicationRoutes = {
    basvurular: `/yonetim/basvurular`,
    yonlendirilen: `/yonetim/yonlendirilen-basvurular`,
  };

  const reportRoutes = {
    raporlar: `/yonetim/raporlar`,
    anketler: `/yonetim/anketler`,
  };

  const opsRoutes = {
    gorevAtama: `${basePath}/gorev-atama`,
  };

  //   Halk Masası rotaları
  const hmRoutes = {
    
    lookup: "/yonetim/halk-masasi/sorgu",
    create: "/yonetim/halk-masasi/yeni-basvuru",
  };

  
  /* -------------------------------------------------------------
     MENÜ SECTIONS
     - Halk Masası rolünde sadece HM menüsü görünür
     - Diğer roller mevcut menüyle devam eder
  ------------------------------------------------------------- */
  const sections = useMemo(() => {
    //   Halk Masası özel menü
        if (isHalkMasasi) {
      return [
        {
          key: "hm_manage",
          label: "Başvuru Yönetimi",
          items: [
            { key: "hm_create", label: "Başvuru Oluştur", to: hmRoutes.create, icon: PlusCircleIcon },
          ],
        },
        {
          key: "hm_lookup",
          label: "Başvuru Sorgula",
          items: [
            { key: "hm_lookup", label: "Sorgu", to: hmRoutes.lookup, icon: MagnifyingGlassIcon },
          ],
        },
      ];
    }

    //   Mevcut davranış: Başkan / birim rolleri
    return [
      {
        key: "applications",
        label: "Başvuru Yönetimi",
        items: [
          { key: "basvurular", label: "Başvurular", to: applicationRoutes.basvurular, icon: InboxArrowDownIcon },
          { key: "forwarded", label: "Yönlendirilenler", to: applicationRoutes.yonlendirilen, icon: ArrowPathRoundedSquareIcon },
        ],
      },
      {
        key: "reporting",
        label: "Analiz",
        items: [
          { key: "reports", label: "Raporlar", to: reportRoutes.raporlar, icon: PresentationChartBarIcon },
          ...(isBaşkan
            ? [{ key: "surveys", label: "Anketler", to: reportRoutes.anketler, icon: ClipboardDocumentCheckIcon }]
            : []),
        ],
      },
      {
        key: "operations",
        label: "Operasyon",
        items: [{ key: "assignments", label: "Talep Atama", to: opsRoutes.gorevAtama, icon: TaskIcon }],
      },
    ];
  }, [
    isHalkMasasi,
    isBaşkan,
    applicationRoutes.basvurular,
    applicationRoutes.yonlendirilen,
    reportRoutes.raporlar,
    reportRoutes.anketler,
    opsRoutes.gorevAtama,
   
    hmRoutes.lookup,
    hmRoutes.create,
   
  ]);

  /* -------------------------------------------------------------
     BİRLEŞİK SIDEBAR (2026-07-05) — menü NAV sözleşmesinden İZİN-türevli üretilir.
     Rol kontrolü YOKTUR. Artık TEK workspace değil: kullanıcının izinli olduğu
     TÜM gruplar (belediye + Halk Masası + Sistem …) tek sidebar'da birleşir
     (visibleNav unified=true). belediye (relative href'li) kalemlerin tabanı =
     GERÇEK birim; HM/Sistem kalemleri kendi absolute href'lerini kullanır.
     Saf saha/HM deneyimi korunur: o kullanıcıların yalnız kendi grubu görünür
     (başka izinleri yok); saha zaten sidebar'sız kabukta.
  ------------------------------------------------------------- */
  const izinliSections = useMemo(() => {
    if (!uiOn) return null;
    const isNonBelediyeWs = unitSlug === "halk-masasi" || unitSlug === "sistem";
    const activeSlug =
      typeof window !== "undefined"
        ? window.localStorage.getItem("activeUnitSlug") || ""
        : "";
    const belediyeBase = !isNonBelediyeWs
      ? `/yonetim/${unitSlug || ""}`.replace(/\/+$/, "")
      : scope?.mode === "all_units"
      ? "/yonetim/baskanlik"
      : `/yonetim/${activeSlug}`.replace(/\/+$/, "");
    const sectionsFromNav = buildSectionsFromNav(
      visibleNav(permissions, { unified: true, menuOnly: true }),
      belediyeBase
    );
    // FAZ-28+ modül kapısı: internal_tasks / mesajlasma KAPALIYKEN ilgili
    // maddeler menüde İZ BIRAKMAZ (izin dursa bile). enabled_modules /me'den;
    // eksikse açık say.
    // FAZ-52: "Mesajlar" maddesi menüden tamamen kaldırıldı (sayfa da silindi) —
    // mesajlaşmanın tek yüzeyi sohbet penceresi. Geriye yalnız görev kapısı kaldı.
    const gorevOn = !enabledModules || enabledModules.internal_tasks !== false;
    if (gorevOn) return sectionsFromNav;
    // FAZ-56: kurum içi görev maddelerinin İKİSİ DE düşer. "gorevlerim"
    // anahtarı saha personelinin ATAMA sayfasıdır (assignments) —
    // internal_tasks modülüyle ilgisi yok, ona dokunulmaz.
    const gizli = new Set(["gorev-ata", "gorev-bana"]);
    return sectionsFromNav
      .map((s) => ({ ...s, items: s.items.filter((i) => !gizli.has(i.key)) }))
      .filter((s) => s.items.length > 0);
  }, [uiOn, permissions, unitSlug, scope, enabledModules]);

  const effectiveSections = uiOn && izinliSections ? izinliSections : sections;

  /* -------------------------------------------------------------
     GENEL > ANASAYFA — tek noktadan enjeksiyon (her iki yolu da kapsar).
     Anasayfa artık navbar'da değil, sidebar'ın en üstünde "Genel" başlığı
     altında. Saha personeli zaten sidebar'sız kabukta olduğundan görünmez;
     yine de güvence için saha ve sistem workspace'i hariç tutulur.
  ------------------------------------------------------------- */
  const homeTo = useMemo(() => {
    // uiOn=false → ESKİ (legacy rol) davranış BİREBİR korunur.
    if (!uiOn) {
      if (user?.role === "saha_personeli") return null;
      if (isHalkMasasi) return "/yonetim/halk-masasi";
      // FAZ-22 4b: canonical (birim localStorage/scope'tan; başkan/birim tek hedefe katlanır)
      if (isBaşkan) return "/yonetim/dashboard";
      if (unitSlug && unitSlug !== "sistem" && unitSlug !== "halk-masasi") {
        return "/yonetim/dashboard";
      }
      return null;
    }

    // uiOn=true → İZİN/PROFİL türevli (legacy role'e BAĞIMSIZ). Kullanıcı kararı
    // (2026-07-10): saha personeli DIŞINDA HERKESTE Anasayfa görünür. Hedef, rolün
    // ERİŞEBİLECEĞİ ekran → yanlış "yetkisiz" düşüşü de olmaz. all_units kullanıcıda
    // unitSlug boş olsa da link kaybolmaz (eski hata buydu).
    const set = new Set(Array.isArray(permissions) ? permissions : []);
    // Saf saha: profil saha VEYA (görev var + başvuru erişimi yok + all_units değil).
    // Saha zaten sidebar'sız kabukta; yine de güvence.
    if (uiProfile === "saha") return null;
    if (
      set.has("assignments.feedback.create") &&
      !set.has("applications.view") &&
      scope?.mode !== "all_units"
    ) {
      return null;
    }
    // Halk Masası: "Sorgu" ekranı ZATEN ilk-karşılayan sayfa (anasayfa) → AYRI
    // "Anasayfa" ENJEKTE ETME (2026-07-10 kullanıcı kararı). NOT: HM tespiti YALNIZ
    // ui_profile ile — halk_masasi.citizen.search BAŞKANDA DA var; onunla tespit
    // edilseydi başkanın Anasayfa'sı yanlışlıkla HM'ye giderdi.
    if (uiProfile === "halk_masasi") return null;
    // Dashboard izni olan → gerçek dashboard (menüde başka yolu yok → Anasayfa anlamlı)
    if (set.has("dashboard.view")) return "/yonetim/dashboard";
    // Sistem yöneticisi → sistem ana ekranı (kullanıcılar)
    if (set.has("users.view") || set.has("roles.manage")) {
      return "/yonetim/sistem/kullanicilar";
    }
    // Başvuru erişimi olan (birim personeli) → başvurular
    if (set.has("applications.view")) return "/yonetim/basvurular";
    return null;
  }, [uiOn, user, isHalkMasasi, isBaşkan, unitSlug, uiProfile, permissions, scope]);

  const sectionsWithHome = useMemo(() => {
    if (!homeTo) return effectiveSections;
    if (effectiveSections.some((s) => s.key === "general")) return effectiveSections;

    const general = {
      key: "general",
      label: "Genel",
      items: [{ key: "home", label: "Anasayfa", to: homeTo, icon: HomeIcon }],
    };
    return [general, ...effectiveSections];
  }, [effectiveSections, homeTo]);

  /* -------------------------------------------------------------
     BİLDİRİMLER — saha personeli DIŞINDA tüm kullanıcılarda sidebar'da
     görünür (bildirim çekirdek/kendi verisi; izin gerektirmez). "Genel"
     grubu varsa oraya, yoksa başa "Genel" grubuyla enjekte edilir.
  ------------------------------------------------------------- */
  const sectionsFinal = useMemo(() => {
    if (user?.role === "saha_personeli") return sectionsWithHome;

    const bildirimItem = {
      key: "bildirimler",
      label: "Bildirimler",
      to: "/yonetim/bildirimler",
      icon: BellIcon,
      // FAZ-85: bu maddenin ikonu okunmamış varken minik kırmızı nokta taşır.
      // ROZET DEĞİL — FAZ-77'de kaldırılan sayı rozetleri geri gelmiyor
      // (kullanıcı: "kaç tane olduğunu orada görmemize gerek yok").
      nokta: true,
    };

    const base = sectionsWithHome.map((s) => ({ ...s, items: [...s.items] }));
    const general = base.find((s) => s.key === "general");
    if (general) {
      if (!general.items.some((i) => i.key === "bildirimler")) {
        general.items.push(bildirimItem);
      }
      return base;
    }
    return [{ key: "general", label: "Genel", items: [bildirimItem] }, ...base];
  }, [sectionsWithHome, user]);

  /* -------------------------------------------------------------
     HANDLERS
  ------------------------------------------------------------- */
  const handleSidebarBackgroundClick = (e) => {
    const isToggle = e.target.closest(".sidebar-toggle");
    const isLink = e.target.closest(".sidebar-link");
    if (isToggle || isLink) return;
    onToggle();
  };

  const handleToggleClick = (e) => {
    e.stopPropagation();
    onToggle();
  };

  /* FAZ-87: telefonda madde seçilince çekmece kapanır.
     FAZ-98 / T-4: TABLETTE DE KAPANIR. FAZ-87'de kapanmıyordu çünkü ray
     İTMELİ idi — açık menü sayfayı daraltıyordu ama örtmüyordu. Artık üste
     biniyor (kullanıcı kararı: "sayfanın şeklini şemalini bozmasın, üstten
     bir pencereymiş gibi açılsın"); açık kalsaydı, gidilen sayfayı örten bir
     pencere bırakırdı. Masaüstü davranışı DEĞİŞMEDİ: orada da kapanıyordu
     (istisna yalnız tabletti), bu yüzden koşul artık istisnasız. */
  const handleNavClick = () => {
    if (isExpanded) onToggle();
  };

  /* -------------------------------------------------------------
     CLASSNAMES
     FAZ-87: "mobile-open" kaldırıldı — tek işi gölge eklemekti.
  ------------------------------------------------------------- */
  const sidebarClasses = ["sidebar"];
  if (isExpanded) sidebarClasses.push("expanded");

  /* -------------------------------------------------------------
     RENDER
  ------------------------------------------------------------- */
  return (
    <>
      {/* FAZ-87 — PERDE. Telefonda çekmecenin, tablette genişletilmiş rayın
          arkasını karartır. Kullanıcı bir katmanın "üstte" olduğunu ancak
          arkası karartılırsa anlar; karartma olmadan sidebar sayfanın bir
          parçası gibi okunuyordu — "neden ekranı yiyor" hissinin kaynağı buydu.
          Masaüstünde çizilmez: orada sidebar zaten sayfanın parçası. */}
      {(telefon || tablet) && (
        <div
          className={["sidebar-perde", isExpanded ? "acik" : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      <aside
        id="belediye-sidebar" /* FAZ-88: navbar hamburger'ının aria-controls hedefi */
        ref={sidebarRef}
        className={sidebarClasses.join(" ")}
        onClick={handleSidebarBackgroundClick}
        /* FAZ-87: telefonda gerçek bir diyalog. Tablette DEĞİL — orası
           kalıcı gezinme rayı, ona "dialog" demek yanlış olurdu. */
        role={telefon ? "dialog" : undefined}
        aria-modal={telefon ? "true" : undefined}
        aria-label={telefon ? "Menü" : undefined}
        aria-hidden={telefon && !isExpanded ? "true" : undefined}
      >
      {/* ÜST: TELEFONDA logo + kapat (X) · masaüstünde eski toggle.
          Kullanıcı (2026-08-01): "sidebar açılınca soldan logo kısmı olsun,
          logo açılan pencereden gelsin; hamburger menü kısmı X işaretine
          dönsün ve tıklayınca kapansın, veya boş bir yere tıklayınca."
          Boş yere tıklama zaten perdeyle çalışıyordu (FAZ-87).

          FAZ-93 m2'de TABLETTE HİÇ ÇİZİLMİYORDU: navbarın hamburger'ı (FAZ-88)
          rayı açıp kapattığı için rayın tepesindeki ☰ ikinci bir düğmeydi.

          FAZ-98 / T-3 — GERİ GELDİ, ÇÜNKÜ TEKİ KALKTI. Kullanıcı (2026-08-02):
          "yukarıdaki hamburger menü yerine logo olsun; hamburger menüyü normal
          web'deki gibi sidebar'ın üstüne [al]." Tablet navbarı artık masaüstü
          navbarı (T-1) ve onda hamburger yok — tek ☰ yine tek, ama bu kez
          web'in durduğu yerde. Aynı düğme, farklı ev; çift değil. */}
      <div
        className={["sidebar-top", telefon ? "sidebar-top--telefon" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        {telefon && (
          <img src={kimLogo} alt="Arsuz Belediyesi" className="sidebar-marka" />
        )}
        <button
          type="button"
          className="sidebar-toggle"
          onClick={handleToggleClick}
          aria-label={telefon ? "Menüyü kapat" : "Menüyü aç/kapat"}
          title={telefon ? "Kapat" : "Menü"}
        >
          {telefon ? (
            <XMarkIcon className="sidebar-toggle-icon" />
          ) : (
            <Bars3Icon className="sidebar-toggle-icon" />
          )}
        </button>
      </div>

      {/* MENÜ GRUPLARI */}
      <nav className="sidebar-links">
        {sectionsFinal.map((section) => {
          if (!section.items || section.items.length === 0) return null;

          return (
            <div key={section.key} className="sidebar-section">
              <div className="sidebar-section-title">{section.label}</div>

              <div className="sidebar-section-items">
                {section.items.map((item) => {
                  const Icon = item.icon;

                  //   Disabled item (Son Vatandaş yoksa)
                  if (item.disabled) {
                    return (
                      <div
                        key={item.key}
                        className={["sidebar-link", "disabled"].join(" ")}
                        aria-disabled="true"
                        title="Henüz seçili vatandaş yok"
                      >
                        <Icon className="sidebar-icon" />
                        <span className="sidebar-link-label">{item.label}</span>
                      </div>
                    );
                  }

                  return (
                    <NavLink
                      key={item.key}
                      to={item.to}
                      className={({ isActive }) =>
                        ["sidebar-link", isActive ? "active" : ""].filter(Boolean).join(" ")
                      }
                      onClick={handleNavClick}
                    >
                      {/* FAZ-85 — İKONUN ÜSTÜNDE NOKTA.
                          Nokta ikonun KENDİSİNE bağlıdır (sarmalayıcı span),
                          satırın sonuna değil: menü daraltılınca (yalnız ikon
                          modu) etiket kaybolur, satır sonuna konmuş bir işaret
                          de onunla birlikte kaybolurdu. */}
                      {item.nokta ? (
                        <span className="sidebar-icon-wrap">
                          <Icon className="sidebar-icon" />
                          {hasUnread ? (
                            <span className="sidebar-dot" aria-hidden="true" />
                          ) : null}
                        </span>
                      ) : (
                        <Icon className="sidebar-icon" />
                      )}
                      <span className="sidebar-link-label">{item.label}</span>
                      {/* FAZ-77 — MENÜ ROZETLERİ KALDIRILDI (kullanıcı kararı
                          2026-07-30): "Görev Ata ve Görevlerim yanındaki 1-2-3
                          gibi şeyleri kaldır, kaç tane görev olduğunu orada
                          görmemize gerek yok."
                          Rozet aynı zamanda menüyü canlı bir veri yüzeyi
                          yapıyordu: her rota değişiminde iki ayrı sayaç isteği
                          (fetchCounters) atılıyordu. Menü artık yalnız menü. */}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* FOOTER — MapData */}
      <div className="sidebar-footer" onClick={(e) => e.stopPropagation()}>
        <a
          className="sidebar-footer-logo"
          href="https://mapdataglobal.com"
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          aria-label="Dogansoft"
          title="Dogansoft"
        >
          <img src={logo} alt="Dogansoft" />
        </a>

        {/* FAZ-92: logonun altındaki "Dogansoft" YAZISI kaldırıldı
            (kullanıcı: "sidebarda tekrardan logonun altında Dogansoft
            yazmaya gerek yok"). Logo zaten aynı adrese gidiyor ve markayı
            kendisi taşıyor — aynı bilgi iki kez yazılmıyor. */}
      </div>
      </aside>
    </>
  );
};

export default BelediyeSidebar;